import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { PawPrint, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const googleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/studio";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function AuthPage({ mode }) {
  const { user, signIn } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const signup = mode === "signup";

  if (user) return <Navigate to="/studio" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = signup ? form : { email: form.email, password: form.password };
      const { data } = await api.post(signup ? "/auth/register" : "/auth/login", body);
      signIn(data);
      nav("/studio");
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <main className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2" data-testid="auth-page">
      <div className="hidden lg:block relative overflow-hidden bg-[#FF5A79]">
        <img src="https://images.unsplash.com/photo-1629740067905-bd3f515aa739?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80" alt="" className="absolute inset-0 h-full w-full object-cover mix-blend-multiply opacity-80" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-display text-5xl font-bold leading-tight">Every pet deserves a portrait.</p>
          <p className="mt-4 text-white/85">Join thousands of pet parents making their besties go viral.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md rise">
          <PawPrint className="h-10 w-10 text-[#FF5A79]" />
          <h1 className="mt-4 text-4xl font-bold tracking-tight">{signup ? "Create your account" : "Welcome back"}</h1>
          <p className="mt-2 text-[#6E687A]">{signup ? "5 free AI creations every single day." : "Your pets missed you."}</p>

          <button onClick={googleLogin} data-testid="google-login-button" type="button"
            className="mt-8 w-full flex items-center justify-center gap-3 rounded-full bg-white sticker sticker-press py-3 font-bold">
            <svg className="h-5 w-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
            Continue with Google
          </button>
          <div className="my-6 flex items-center gap-4 text-xs font-semibold uppercase tracking-wider text-[#6E687A]"><span className="h-px flex-1 bg-[#EAE3D9]" />or<span className="h-px flex-1 bg-[#EAE3D9]" /></div>

          <form onSubmit={submit} className="space-y-4">
            {signup && (
              <div className="space-y-1.5"><Label htmlFor="name">Your name</Label>
                <Input id="name" required value={form.name} onChange={set("name")} className="h-12 rounded-xl bg-white" data-testid="auth-name-input" /></div>
            )}
            <div className="space-y-1.5"><Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={set("email")} className="h-12 rounded-xl bg-white" data-testid="auth-email-input" /></div>
            <div className="space-y-1.5"><Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="h-12 rounded-xl bg-white" data-testid="auth-password-input" /></div>
            {error && <p className="text-sm font-semibold text-red-600" data-testid="auth-error">{error}</p>}
            <button disabled={busy} type="submit" data-testid="auth-submit-button"
              className="w-full flex items-center justify-center gap-2 rounded-full bg-[#FF5A79] text-white sticker sticker-press py-3 font-bold disabled:opacity-70">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}{signup ? "Sign up free" : "Log in"}
            </button>
          </form>
          <p className="mt-6 text-sm text-center text-[#6E687A]">
            {signup ? "Already have an account? " : "New to Cutie Parlor? "}
            <Link to={signup ? "/login" : "/signup"} className="font-bold text-[#FF5A79]" data-testid="auth-switch-link">{signup ? "Log in" : "Create one"}</Link>
          </p>
        </div>
      </div>
    </main>
  );
}
